import { forwardRef } from 'react'
import { cn } from '@/lib/utils'

// -------------------------------------------------------
// Card root
// -------------------------------------------------------

interface CardExtraProps {
  /** Legacy padding prop for backwards compatibility */
  padding?: 'none' | 'sm' | 'md' | 'lg'
  hover?: boolean
}

const paddingMap: Record<NonNullable<CardExtraProps['padding']>, string> = {
  none: '',
  sm: 'p-3',
  md: 'p-5',
  lg: 'p-6',
}

const Card = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement> & CardExtraProps>(
  ({ className, padding, hover, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        // Estetica Apple: squircle morbido, bordo tenue, ombra soffusa diffusa.
        'rounded-2xl border border-slate-100 dark:border-slate-700/70 bg-white dark:bg-slate-800 shadow-card',
        padding && paddingMap[padding],
        // Hover: lieve elevazione soffusa (motion morbido tipo Apple).
        hover && 'hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] cursor-pointer',
        className
      )}
      {...props}
    />
  )
)
Card.displayName = 'Card'

// -------------------------------------------------------
// CardHeader - supports both shadcn-style (DOM attrs) and
// legacy object-based props (title, description, action)
// -------------------------------------------------------

interface CardHeaderLegacyProps {
  title: string
  description?: string
  action?: React.ReactNode
  className?: string
}

type CardHeaderProps =
  | (React.HTMLAttributes<HTMLDivElement> & { title?: never })
  | CardHeaderLegacyProps

function CardHeader(props: CardHeaderProps) {
  // Legacy usage: <CardHeader title="..." description="..." action={...} />
  if ('title' in props && props.title !== undefined) {
    const { title, description, action, className } = props as CardHeaderLegacyProps
    return (
      <div className={cn('flex items-start justify-between mb-4 p-6 pb-0', className)}>
        <div>
          <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
          {description && (
            <p className="text-xs text-slate-500 mt-0.5">{description}</p>
          )}
        </div>
        {action && <div className="flex-shrink-0">{action}</div>}
      </div>
    )
  }

  // shadcn-style usage: <CardHeader><CardTitle>...</CardTitle></CardHeader>
  const { className, ...rest } = props as React.HTMLAttributes<HTMLDivElement>
  return (
    <div
      className={cn('flex flex-col space-y-1.5 p-6', className)}
      {...rest}
    />
  )
}
CardHeader.displayName = 'CardHeader'

// -------------------------------------------------------
// CardTitle
// -------------------------------------------------------

const CardTitle = forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn('text-lg font-semibold leading-none tracking-tight text-slate-900', className)}
      {...props}
    />
  )
)
CardTitle.displayName = 'CardTitle'

// -------------------------------------------------------
// CardDescription
// -------------------------------------------------------

const CardDescription = forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p
      ref={ref}
      className={cn('text-sm text-slate-500', className)}
      {...props}
    />
  )
)
CardDescription.displayName = 'CardDescription'

// -------------------------------------------------------
// CardContent
// -------------------------------------------------------

const CardContent = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('p-6 pt-0', className)}
      {...props}
    />
  )
)
CardContent.displayName = 'CardContent'

// -------------------------------------------------------
// CardFooter
// -------------------------------------------------------

const CardFooter = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex items-center p-6 pt-0', className)}
      {...props}
    />
  )
)
CardFooter.displayName = 'CardFooter'

export { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter }
